import { z } from 'zod';
/**
 * Board geometry and content schemas.
 *
 * These schemas are the single source of truth for the board document: the web
 * client uses the inferred types, and the server validates every incoming
 * operation against them before touching a document. Anything not described
 * here is stripped, so a malicious client cannot smuggle extra fields into
 * stored state.
 */
export const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const hex = z.string().regex(HEX_COLOR, 'expected #rrggbb');
const finite = z.number().finite();
/** World-space coordinate. Bounded so a bad client cannot poison the bbox math. */
const coord = finite.min(-1e7).max(1e7);
const size = finite.min(1).max(1e6);
export const ACCENT_COLORS = ['#A78BFA', '#5B8CFF', '#22D3EE', '#34D399', '#FFB454'];
const id = z.string().min(1).max(64);
const itemBase = { id, x: coord, y: coord, w: size, h: size, z: finite };
export const frameItemSchema = z.object({
    ...itemBase,
    type: z.literal('frame'),
    label: z.string().max(200),
});
/** Optional whole-element typography; legacy documents keep their original defaults. */
export const textStyleSchema = z.object({
    fontFamily: z.enum(['sans', 'serif', 'mono']).optional(),
    fontSize: finite.min(8).max(160).optional(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    color: hex.optional(),
});
export const stickyItemSchema = z.object({
    ...itemBase,
    type: z.literal('sticky'),
    color: hex,
    text: z.string().max(10_000),
    textStyle: textStyleSchema.nullable().optional(),
});
export const textItemSchema = z.object({
    ...itemBase,
    type: z.literal('text'),
    size: z.enum(['s', 'm', 'l']),
    text: z.string().max(10_000),
    textStyle: textStyleSchema.nullable().optional(),
});
export const imageItemSchema = z.object({
    ...itemBase,
    type: z.literal('image'),
    /** Asset row id when the image is an upload; absent for built-in placeholders. */
    assetId: z.string().max(64).optional(),
    src: z.string().max(500_000),
    name: z.string().max(300),
});
export const linkItemSchema = z.object({
    ...itemBase,
    type: z.literal('link'),
    title: z.string().max(300),
    url: z.string().max(2000),
});
/** A grouped child. Coordinates are relative to the group's own origin. */
export const groupChildSchema = z.object({
    id,
    rx: coord,
    ry: coord,
    w: size,
    h: size,
    type: z.enum(['sticky', 'text', 'image', 'link']),
    color: hex.optional(),
    text: z.string().max(10_000).optional(),
    textStyle: textStyleSchema.nullable().optional(),
    size: z.enum(['s', 'm', 'l']).optional(),
    src: z.string().max(500_000).optional(),
    name: z.string().max(300).optional(),
    title: z.string().max(300).optional(),
    url: z.string().max(2000).optional(),
    assetId: z.string().max(64).optional(),
});
export const groupItemSchema = z.object({
    ...itemBase,
    type: z.literal('group'),
    name: z.string().max(200),
    children: z.array(groupChildSchema).max(500),
});
export const itemSchema = z.discriminatedUnion('type', [
    frameItemSchema,
    stickyItemSchema,
    textItemSchema,
    imageItemSchema,
    linkItemSchema,
    groupItemSchema,
]);
export const strokeSchema = z.object({
    id,
    pts: z
        .array(z.tuple([coord, coord]))
        .min(1)
        .max(5000),
    color: hex,
    w: finite.min(0.1).max(200),
});
export const connSchema = z.object({ id, a: id, b: id });
export const commentMsgSchema = z.object({
    id,
    who: z.string().max(120),
    text: z.string().min(1).max(4000),
    at: z.number().int().nonnegative(),
});
export const commentSchema = z.object({
    id,
    x: coord,
    y: coord,
    author: z.string().max(120),
    msgs: z.array(commentMsgSchema).max(500),
});
export const boardDocSchema = z.object({
    items: z.array(itemSchema).max(5000).default([]),
    strokes: z.array(strokeSchema).max(5000).default([]),
    conns: z.array(connSchema).max(5000).default([]),
    comments: z.array(commentSchema).max(2000).default([]),
});
export const emptyDoc = () => ({ items: [], strokes: [], conns: [], comments: [] });
//# sourceMappingURL=types.js.map